import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Lightbulb, CalendarDays, List, MessageSquare, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useFetch, usePaged } from "@/lib/hooks";
import { Pager } from "@/components/core/Pager";
import { CANALI, STATI_POST, statoPostOf } from "@/lib/ped";
import { Panel } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { CoreCalendar } from "@/components/calendar/CoreCalendar";
import { ChannelIcons } from "@/components/ped/PedBits";
import { PedList } from "@/components/ped/PedList";
import { PostEditorDialog } from "@/components/ped/PostEditorDialog";
import { AttachmentsPanel } from "@/components/core/AttachmentsPanel";
import { useAuth } from "@/context/AuthContext";
import { BestTimes } from "@/components/adminai/BestTimes";
import { BatchScriptDialog } from "@/components/adminai/BatchScriptDialog";
import { IdeasDialog } from "@/components/ped/IdeasDialog";

const Filter = ({ value, onChange, options, all, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-[170px] rounded-xl bg-[#A6DAFC]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

const PostChip = ({ arg }) => {
  const p = arg.event.extendedProps.post;
  return (
    <div className="flex items-center gap-1 overflow-hidden px-0.5 text-white" data-testid={`ped-chip-${p.id}`}>
      <span className="text-[10px] opacity-80">{arg.timeText}</span>
      <span className="bg-white/90 rounded px-0.5 inline-flex"><ChannelIcons canali={p.canali} size={10} /></span>
      {p.modifiche_richieste && <span className="h-2 w-2 shrink-0 rounded-full bg-[#E8173A] ring-2 ring-white" title="Modifiche richieste dal cliente" data-testid={`ped-chip-changes-${p.id}`} />}
      <span className="truncate text-[11px]">{p.titolo}</span>
      {p.commenti > 0 && <span className="ml-auto shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold bg-white/25 rounded px-1" data-testid={`ped-chip-comments-${p.id}`}><MessageSquare className="h-2.5 w-2.5" />{p.commenti}</span>}
    </div>
  );
};

export const ClientPed = ({ client, users }) => {
  const [sp, setSp] = useSearchParams();
  const [view, setView] = useState("calendario");
  const [canale, setCanale] = useState("all");
  const [stato, setStato] = useState("all");
  const [mese, setMese] = useState("");
  const [pedMese, setPedMese] = useState(new Date().toISOString().slice(0, 7));
  const [editor, setEditor] = useState({ open: false, post: null, defaults: null });
  const [ideas, setIdeas] = useState(false);
  const [batch, setBatch] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [del, setDel] = useState(null);

  const [range, setRange] = useState(null);
  const onRange = (r) => setRange((old) => (old && old.da === r.da && old.a === r.a ? old : r));
  const p = new URLSearchParams({ client_id: client.id });
  if (canale !== "all") p.set("canale", canale);
  if (stato !== "all") p.set("stato", stato);
  const lp = Object.fromEntries(p);
  if (mese) lp.mese = mese;
  const list = view === "elenco";
  const paged = usePaged(list ? "/posts" : null, lp, 50);
  const cal = useFetch(!list && range ? `/posts?${p.toString()}&da=${encodeURIComponent(range.da)}&a=${encodeURIComponent(range.a)}` : null);
  const posts = list ? paged.items : cal.data;
  const reload = list ? paged.reload : cal.reload;

  useEffect(() => {
    const pid = sp.get("post");
    if (!pid || !posts) return;
    const found = posts.find((x) => x.id === pid);
    if (found) setEditor({ open: true, post: found, defaults: null });
    sp.delete("post"); setSp(sp, { replace: true });
  }, [posts, sp, setSp]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const onStatus = (post, v) => act(() => api.patch(`/posts/${post.id}/status`, { stato: v }), "Stato aggiornato");
  const onDuplicate = (post) => act(() => api.post(`/posts/${post.id}/duplicate`), "Post duplicato come bozza");
  const remove = () => { act(() => api.delete(`/posts/${del.id}`), "Post eliminato"); setDel(null); };

  const onDrop = async (info) => {
    try {
      await api.patch(`/posts/${info.event.id}/reschedule`, { data_pubblicazione: info.event.start.toISOString() });
      toast.success("Post riprogrammato"); reload();
    } catch (e) { info.revert(); toast.error(errMsg(e)); }
  };

  const events = (posts || []).map((x) => {
    const s = statoPostOf(x.stato);
    return { id: x.id, title: x.titolo, start: x.data_pubblicazione, backgroundColor: s.color, borderColor: s.color, extendedProps: { kind: "post", post: x } };
  });

  return (
    <div data-testid="client-ped">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-slate-200 bg-[#A6DAFC] p-1">
            {[["calendario", "Calendario", CalendarDays], ["elenco", "Elenco", List]].map(([k, l, I]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`ped-view-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${view === k ? "bg-[#0A1128] text-white" : "text-slate-600 hover:text-[#0A1128]"}`}><I className="h-4 w-4" />{l}</button>
            ))}
          </div>
          <Filter value={canale} onChange={setCanale} all="Tutti i canali" testid="ped-filter-canale" options={CANALI} />
          <Filter value={stato} onChange={setStato} all="Tutti gli stati" testid="ped-filter-stato" options={STATI_POST} />
          {view === "elenco" && <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="w-[170px] rounded-xl bg-[#A6DAFC]" data-testid="ped-filter-mese" />}
        </div>
        <div className="flex gap-2">
          {isAdmin && <Button variant="outline" onClick={() => setBatch(true)} className="rounded-full font-bold text-[#0A1128] border-[#0A1128]/25" data-testid="ped-batch-scripts"><Clapperboard className="h-4 w-4 mr-1.5 text-[#E8173A]" />Script in serie</Button>}
          <Button variant="outline" onClick={() => setIdeas(true)} className="rounded-full font-bold text-[#0A1128] border-[#0A1128]/25" data-testid="ped-ideas-button"><Lightbulb className="h-4 w-4 mr-1.5 text-[#E8173A]" />Idee PED</Button>
          <Button onClick={() => setEditor({ open: true, post: null, defaults: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="add-post-button"><Plus className="h-4 w-4 mr-1" />Nuovo post</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-3" data-testid="ped-legend">
        {STATI_POST.map((s) => <span key={s.value} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />{s.label}</span>)}
        <span className="text-xs text-slate-400">Trascina un post per cambiarne la data</span>
      </div>
      {isAdmin && <BestTimes clientId={client.id} />}
      {isAdmin && <BatchScriptDialog open={batch} onOpenChange={setBatch} clientId={client.id}
        candidates={(posts || []).slice(0, 60).map((x) => ({ key: `post-${x.id}`, label: x.titolo, sub: `${new Date(x.data_pubblicazione).toLocaleDateString("it-IT")} · ${x.formato}${x.script_id ? " · script collegato" : ""}`, payload: { tipo: "post", post_id: x.id } }))} />}
      <details className="mb-4 rounded-2xl border border-[#5FB3EA] bg-[#A6DAFC]/60 p-4 group" data-testid="ped-files">
        <summary className="cursor-pointer select-none font-display text-lg font-extrabold text-[#0A1128] min-h-[32px]" data-testid="ped-files-toggle">File del PED <span className="text-sm font-semibold text-slate-600">(riepiloghi mensili, PDF, Excel, PowerPoint)</span></summary>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input type="month" value={pedMese} onChange={(e) => setPedMese(e.target.value)} className="w-[180px] rounded-xl bg-white" data-testid="ped-files-filter-mese" />
          <Button type="button" variant="outline" size="sm" onClick={() => setPedMese("")} className="rounded-full font-bold" data-testid="ped-files-filter-all">Tutti</Button>
        </div>
        <div className="mt-3"><AttachmentsPanel tipo="ped" clientId={client.id} mese={pedMese} ped title={pedMese ? "File del mese" : "Tutti i file"} testid="ped-files-panel" /></div>
      </details>
      {view === "calendario" ? (
        <Panel className="p-5">
          <CoreCalendar events={events} editable onEventDrop={onDrop} eventContent={(arg) => <PostChip arg={arg} />} views="dayGridMonth,listMonth" onRangeChange={onRange}
            onEventClick={(ev) => setEditor({ open: true, post: ev.extendedProps.post, defaults: null })}
            onSelect={(info) => setEditor({ open: true, post: null, defaults: { date: info.start, allDay: info.allDay } })} />
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          <PedList posts={posts} onEdit={(post) => setEditor({ open: true, post, defaults: null })} onDuplicate={onDuplicate} onDelete={setDel} onStatus={onStatus} onBulkDone={reload}
            emptyAction={<Button onClick={() => setEditor({ open: true, post: null, defaults: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="ped-empty-new"><Plus className="h-4 w-4 mr-1" />Nuovo post</Button>} />
          <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="ped-pager" />
        </Panel>
      )}
      <PostEditorDialog open={editor.open} onOpenChange={(o) => setEditor((x) => ({ ...x, open: o }))} post={editor.post} defaults={editor.defaults}
        clientId={client.id} users={users} onSaved={reload} />
      <IdeasDialog open={ideas} onOpenChange={setIdeas} client={client} onInserted={reload} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il post?" description={`Il post «${del?.titolo}» verrà eliminato dal PED.`} onConfirm={remove} />
    </div>
  );
};
